import moment from "moment";
import {
  displayCommercialFields,
  displayFedVio,
  isAddressComplete,
  isLicenseValid,
  isOfficerStateIdNumberValid,
  isTicketWarning,
  isValidDateString,
  missingLastName,
  getAgeInYears,
} from "../../utils/utils";
import { calculatePenalty } from "../../utils/penalty-calculation";

export const UNDER_17_RELIEF_ACT_TYPE_NAME =
  "Defendant is under 17 years of age";

export const isUnder17OnDate = (dob, compareDate) => {
  const age = getAgeInYears(dob, compareDate);
  return age !== null && age >= 0 && age < 17;
};

const isInvalidUnder17ReliefActType = ticket =>
  ticket?.reliefActType?.name === UNDER_17_RELIEF_ACT_TYPE_NAME &&
  !isUnder17OnDate(
    ticket?.issuedTo?.personalAttribute?.dob,
    ticket?.violationDate,
  );

export const returnTicketValidationObj = (
  ticket,
  incidents,
  incidentIndex,
  ticketLocation,
  juvenile,
) => {
  const licenseNumber = ticket?.issuedTo?.personalAttribute?.driversLicense
    ? ticket?.issuedTo?.personalAttribute?.driversLicense
    : incidents[incidentIndex]?.personIncidentSpecifics?.find(
        pis => pis?.person?.id === ticket?.issuedTo?.id,
      )?.person?.personalAttribute?.driversLicense;
  const licenseStateStr = ticket.issuedTo?.personalAttribute?.licenseState?.name
    ? ticket.issuedTo?.personalAttribute?.licenseState?.name
    : incidents[incidentIndex]?.personIncidentSpecifics?.find(
        pis => pis?.person?.id === ticket?.issuedTo?.id,
      )?.person?.personalAttribute?.licenseState?.name;
  const weightPenaltyContext = {
    isWeightViolation: displayCommercialFields(
      ticket?.ticketViolationPrototype?.ticketCategory?.name,
    ),
    weightLimit: ticket?.weightLimit,
    violationOccurrence: ticket?.violationOccurrence,
    violationName: ticket?.ticketViolationPrototype?.name,
  };
  const violationSelected = !!ticket?.ticketViolationPrototype?.fullName;
  const isWarning =
    violationSelected && isTicketWarning(ticket?.ticketViolationPrototype);
  const ticketOutcomeName = ticket?.ticketOutcomeType?.name?.toLowerCase();
  const ticketOutcomeIsWarning =
    ticketOutcomeName?.includes("warning") ?? false;
  const isOrganization =
    ticket?.issuedTo?.personalAttribute?.organization === true;
  const isUnder17 =
    !isOrganization &&
    isUnder17OnDate(
      ticket?.issuedTo?.personalAttribute?.dob,
      ticket?.violationDate,
    );
  const organizationReliefActSelected =
    ticket?.reliefActType?.name?.toLowerCase().includes("organization") ?? false;
  const penalty = calculatePenalty(
    ticket?.actualSpeed,
    ticket?.postedLimit,
    ticket?.waiver,
    ticket?.points,
    ticket?.penaltyMin,
    ticket?.penaltyMax,
    ticket?.cvHazardFee,
    ticket?.seatBeltOccurrence,
    isWarning,
    ticket?.ticketViolationPrototype?.speedingVio,
    weightPenaltyContext,
  );
  let validationObj = {
    violationLocation: ticketLocation?.id !== "",
    violationLocationIsComplete: isAddressComplete(ticket?.ticketLocation),
    municipality: !!ticket?.municipality?.id,
    issuedTo: !!ticket?.issuedTo?.id,
    validDob: ticket?.issuedTo?.personalAttribute?.organization
      ? true
      : isValidDateString(ticket?.issuedTo?.personalAttribute?.dob),
    validLastName: !missingLastName(ticket?.issuedTo),
    mailingAddress: !!ticket?.issuedToAddress?.id,
    mailingAddressIsComplete: isAddressComplete(ticket?.issuedToAddress),
    validMailingAddress: ticket?.mailingAddressVerified,
    defendantDidThen:
      !!ticket?.defendantActions && ticket?.defendantActions?.trim() !== "",
    reliefActType: !!ticket?.reliefActType?.id,
    under17ReliefActType: !isInvalidUnder17ReliefActType(ticket),
    under17OnlyReliefActType:
      !isUnder17 ||
      !ticket?.reliefActType?.id ||
      ticket?.reliefActType?.name === UNDER_17_RELIEF_ACT_TYPE_NAME,
    organizationReliefActType:
      !isOrganization ||
      !ticket?.reliefActType?.id ||
      organizationReliefActSelected,
    individualReliefActType: isOrganization || !organizationReliefActSelected,
    deliveredTo: !!ticket?.deliveredToType?.id,
    violationSelected,

    //if it's an organization don't require a license number or state
    validLicenseNumber: ticket?.issuedTo?.personalAttribute?.organization
      ? true
      : licenseNumber || licenseStateStr
        ? isLicenseValid(licenseNumber, licenseStateStr)?.success
        : true,
    licenseStateSet: ticket?.issuedTo?.personalAttribute?.organization
      ? true
      : licenseNumber || licenseStateStr
        ? !!licenseStateStr
        : true,
    cdlOrganization:
      ticket?.issuedTo?.personalAttribute?.organization && ticket?.cdl
        ? false
        : true,
    actualSpeedAbovePosted:
      ticket?.ticketViolationPrototype?.speedingVio &&
      ticket?.actualSpeed &&
      ticket?.postedLimit
        ? +ticket?.actualSpeed > +ticket?.postedLimit
        : true,
    actualSpeedPresent: ticket?.ticketViolationPrototype?.speedingVio
      ? !!ticket?.actualSpeed && ticket?.actualSpeed > 0
      : true,
    postedSpeedPresent: ticket?.ticketViolationPrototype?.speedingVio
      ? !!ticket?.postedLimit && ticket?.postedLimit > 0
      : true,
    violationDateInPast:
      moment()
        .local()
        .diff(
          moment.utc(ticket?.violationDate, "YYYY-MM-DD HH:mm:ss UTC").local(),
          "seconds",
        ) > 0,
    officerStateIdIsPresent: !!ticket?.officer?.deptAttribute?.stateIdNumber,
    officerStateIdIsValid: isOfficerStateIdNumberValid(
      ticket?.officer?.deptAttribute?.stateIdNumber,
    ),
    violationMatchesOutcome:
      violationSelected &&
      (!ticketOutcomeName || isWarning === ticketOutcomeIsWarning),
    parentAddressPresentOrNotRequired: juvenile
      ? !!ticket?.defendantParent?.personalAttribute?.mailingAddress?.name ||
        !!ticket?.defendantParent?.personalAttribute?.homeAddress?.name
      : true,
    parentAddressCompleteOrNotRequired: juvenile
      ? ticket?.defendantParent?.personalAttribute?.mailingAddress?.name
        ? isAddressComplete(
            ticket?.defendantParent?.personalAttribute?.mailingAddress,
          )
        : isAddressComplete(
            ticket?.defendantParent?.personalAttribute?.homeAddress,
          )
      : true,
    overweightWeight: displayCommercialFields(
      ticket?.ticketViolationPrototype?.ticketCategory?.name,
    )
      ? ticket?.weightLimit
        ? true
        : false
      : true,
    overweightOccurrence: displayCommercialFields(
      ticket?.ticketViolationPrototype?.ticketCategory?.name,
    )
      ? ticket?.violationOccurrence
        ? true
        : false
      : true,
    federalViolationCode: displayFedVio(
      ticket?.ticketViolationPrototype?.ticketCategory?.name,
    )
      ? ticket?.federalViolationCode
        ? true
        : false
      : true,
    deliveredBy: !!ticket?.ticketDeliveryType?.id,
    dateServed: moment
      .utc(ticket?.issuedAt, "YYYY-MM-DD HH:mm:ss UTC")
      .local()
      .isValid(),
    dateServedIsAfterVioDate:
      moment
        .utc(ticket?.issuedAt, "YYYY-MM-DD HH:mm:ss UTC")
        .diff(
          moment.utc(ticket?.violationDate, "YYYY-MM-DD HH:mm:ss UTC"),
          "seconds",
        ) >= 0,
    cmvHasVehicle: ticket?.commercialVehicle ? !!ticket?.vehicle?.id : true,
    ticketHasNonZeroWaiver: isWarning === false ? +penalty > 0 : true,
  };
  //A ticket with no PID AND no state is valid.
  if (!licenseNumber && !licenseStateStr) {
    validationObj.validLicenseNumber = true;
    validationObj.licenseStateSet = true;
  }
  if (
    (licenseNumber && !licenseStateStr) ||
    (licenseStateStr && !licenseNumber)
  ) {
    validationObj.validLicenseNumber = false;
  }

  return validationObj;
};
